/**
 * RecipeMaterialsModal.jsx · v3.2.1 · Feb-2026
 * ─────────────────────────────────────────────────────────────────────────
 * Editor de Receta de un Tipo de Solución. Soporta líneas con:
 *   • match_mode='exact'    → SKU único (product_code + product_name)
 *   • match_mode='category' → Categoría Odoo (product_category_id/name)
 *
 * v3.2.1 · Corazón de la flexibilidad: el modal "Añadir material" ofrece
 * pestañas Exacto | Categoría. En modo categoría el técnico elige el SKU
 * real en campo (SkuSelector) dentro de la categoría autorizada.
 */
import { useState, useEffect, useMemo } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { X, Plus, Trash2, Search, Package, Layers, Loader2, AlertCircle, ChevronRight, ExternalLink } from "lucide-react";

export default function RecipeMaterialsModal({ solutionType, onClose }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPicker, setShowPicker] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [savingId, setSavingId] = useState(null);

  const recipeCategories = useMemo(
    () => items.filter((x) => x.match_mode === "category").map((x) => x.product_category_name).filter(Boolean),
    [items],
  );

  // v93.0.0 · La receta pertenece al TIPO DE SOLUCIÓN (node_id), no al formulario
  const nq = solutionType?.node_id ? `?node_id=${solutionType.node_id}` : "";

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/v1/mesa/solution-types/${solutionType.id}/recipe-lines${nq}`);
      setItems(data.items || []);
    } catch (e) { toast.error("Error cargando receta"); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (solutionType?.id) load(); /* eslint-disable-next-line */ }, [solutionType?.id]);

  const updateField = async (line, field, value) => {
    setSavingId(line.id);
    try {
      await api.put(`/solution-types/${solutionType.id}/materials/${line.id}${nq}`, { [field]: Number(value) });
      if (nq) { await load(); return; }
      setItems(items.map((i) => i.id === line.id ? { ...i, [field]: Number(value) } : i));
    } catch (e) { toast.error("No se pudo guardar"); }
    finally { setSavingId(null); }
  };

  const removeLine = async (line) => {
    const label = line.match_mode === "category" ? line.product_category_name : line.product_name;
    if (!window.confirm(`¿Quitar "${label}" de la receta?`)) return;
    setDeletingId(line.id);
    try {
      await api.delete(`/solution-types/${solutionType.id}/materials/${line.id}${nq}`);
      setItems(items.filter((i) => i.id !== line.id));
      toast.success("Línea eliminada");
    } catch (e) { toast.error(e?.response?.data?.detail || "Error al quitar"); }
    finally { setDeletingId(null); }
  };

  const addExact = async (product) => {
    try {
      await api.post(`/solution-types/${solutionType.id}/materials${nq}`, {
        match_mode: "exact",
        product_code: product.product_code,
        product_name: product.product_name,
        qty_min: 1, qty_max: 1, qty_free: 0,
      });
      await load();
      toast.success("Material exacto agregado");
    } catch (e) { toast.error(e?.response?.data?.detail || "Error al agregar"); }
  };

  const addCategory = async (cat) => {
    const fullName = cat.complete_name || cat.name;
    try {
      await api.post(`/solution-types/${solutionType.id}/materials${nq}`, {
        match_mode: "category",
        product_category_id: cat.id,
        product_category_name: fullName,
        qty_min: 1, qty_max: 1, qty_free: 0,
      });
      await load();
      toast.success(`Categoría agregada · ${fullName}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error al agregar"); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-testid="recipe-modal">
      <div className="relative bg-white rounded-2xl max-w-5xl w-full max-h-[90vh] flex flex-col overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b" style={{ background: "#FBFCE0" }}>
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: "#6B7000" }}>Receta · Materiales</div>
            <h2 className="text-xl font-bold mt-1" style={{ color: "#0A3747" }}>{solutionType?.name}</h2>
            <div className="text-xs text-gray-600 mt-1">
              {items.length === 0
                ? <span className="italic text-gray-400">Receta vacía · añade materiales exactos o por categoría abajo</span>
                : recipeCategories.length > 0
                  ? <>Categorías: <b>{recipeCategories.join(", ")}</b></>
                  : <>Sólo productos exactos ({items.length}) · categorías Odoo opcionales</>}
            </div>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-800" data-testid="recipe-close"><X size={22} /></button>
        </div>

        {/* Body */}
        <div className="p-5 overflow-y-auto flex-1">
          {loading ? (
            <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-gray-400" /></div>
          ) : items.length === 0 ? (
            <div className="py-8 text-center text-gray-500 border-2 border-dashed rounded-xl">
              <Package size={40} className="mx-auto mb-2 text-gray-300" />
              <div className="font-semibold">Sin materiales configurados</div>
              <div className="text-xs">Añade una <b>Categoría</b> (el técnico elige el SKU en campo) o un <b>SKU exacto</b>.</div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold border-b">
                  <tr>
                    <th className="px-2 py-2 text-left w-16">Modo</th>
                    <th className="px-2 py-2 text-left">Producto / Categoría</th>
                    <th className="px-2 py-2 text-center w-16">Mín</th>
                    <th className="px-2 py-2 text-center w-16">Máx</th>
                    <th className="px-2 py-2 text-center w-16">Gratis</th>
                    <th className="px-2 py-2 w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((m) => (
                    <tr key={m.id} className="border-b hover:bg-gray-50" data-testid={`recipe-line-${m.id}`}>
                      <td className="px-2 py-2">
                        {m.match_mode === "category" ? (
                          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-purple-100 text-purple-800 flex items-center gap-1 w-fit">
                            <Layers size={10}/> CATEGORÍA
                          </span>
                        ) : (
                          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 flex items-center gap-1 w-fit">
                            <Package size={10}/> EXACTO
                          </span>
                        )}
                      </td>
                      <td className="px-2 py-2">
                        <div className="font-semibold" style={{ color: "#0A3747" }}>
                          {m.match_mode === "category" ? (m.product_category_name || m.product_name) : m.product_name}
                        </div>
                        <div className="text-[10px] text-gray-500 font-mono">
                          {m.match_mode === "category"
                            ? `Odoo cat #${m.product_category_id} · el técnico elige el SKU en campo`
                            : (m.product_code || "—")}
                        </div>
                        {m.created_by_name && (
                          <div className="text-[9px] text-gray-400 mt-0.5">
                            Creado por <b>{m.created_by_name}</b>{m.created_at ? ` · ${new Date(m.created_at).toLocaleDateString("es-EC", { day:"2-digit", month:"short", year:"numeric" })}` : ""}
                          </div>
                        )}
                      </td>
                      <td className="px-2 py-2 text-center">
                        <NumberEditor value={m.qty_min} saving={savingId === m.id}
                          onSave={(v) => updateField(m, "qty_min", v)} testid={`min-${m.id}`} />
                      </td>
                      <td className="px-2 py-2 text-center">
                        <NumberEditor value={m.qty_max} saving={savingId === m.id}
                          onSave={(v) => updateField(m, "qty_max", v)} testid={`max-${m.id}`} />
                      </td>
                      <td className="px-2 py-2 text-center">
                        <NumberEditor value={m.qty_free} saving={savingId === m.id}
                          onSave={(v) => updateField(m, "qty_free", v)} testid={`free-${m.id}`} />
                      </td>
                      <td className="px-2 py-2">
                        <button onClick={() => removeLine(m)} disabled={deletingId === m.id}
                          className="text-red-500 hover:text-red-700 disabled:opacity-40"
                          data-testid={`remove-line-${m.id}`}>
                          {deletingId === m.id ? <Loader2 size={14} className="animate-spin"/> : <Trash2 size={14}/>}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                {items.some((m) => Number(m.qty_max) < Number(m.qty_min)) && (
                  <tfoot><tr><td colSpan="6" className="pt-2 text-xs text-amber-700 flex items-center gap-1.5">
                    <AlertCircle size={12}/> Hay líneas donde Máx &lt; Mín — revisa las cantidades.
                  </td></tr></tfoot>
                )}
              </table>
            </div>
          )}

          {/* Add buttons */}
          <div className="mt-4 flex items-center gap-2 flex-wrap">
            <button onClick={() => setShowPicker(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold"
              style={{ background: "#0A3747", color: "#E9EE6E" }}
              data-testid="recipe-add-material">
              <Plus size={14}/> Añadir material
            </button>
            {items.length === 0 && (
              <a href="https://alfanet.odoo.com/odoo/inventory/products" target="_blank" rel="noreferrer"
                className="text-xs text-emerald-700 hover:underline flex items-center gap-1"
                data-testid="recipe-configure-categories">
                <ExternalLink size={11}/> Configurar categorías en Odoo
              </a>
            )}
          </div>
        </div>

        {/* Picker overlay */}
        {showPicker && (
          <AddLinePicker
            existingIds={items.filter(i => i.match_mode === "exact").map(i => i.product_code)}
            existingCatIds={items.filter(i => i.match_mode === "category").map(i => i.product_category_id)}
            onAddExact={addExact}
            onAddCategory={addCategory}
            onClose={() => setShowPicker(false)}
          />
        )}
      </div>
    </div>
  );
}

/**
 * AddLinePicker · v3.2.1 · Dos pestañas:
 *   • CATEGORÍA — dropdown Odoo (permite al técnico elegir el SKU en campo)
 *   • EXACTO — SKU único bien identificado
 */
function AddLinePicker({ existingIds, existingCatIds, onAddExact, onAddCategory, onClose }) {
  const [tab, setTab] = useState("category");
  return (
    <div className="absolute inset-0 z-10 bg-white flex flex-col rounded-2xl overflow-hidden" data-testid="add-line-picker">
      <div className="p-4 border-b flex items-center gap-2" style={{ background: "#FBFCE0" }}>
        <div className="text-sm font-bold text-[#0A3747]">Añadir línea de receta</div>
        <div className="flex-1" />
        <button onClick={onClose} className="px-3 py-1.5 border rounded-lg text-sm bg-white hover:bg-gray-50" data-testid="picker-close">Cerrar</button>
      </div>

      {/* Tabs */}
      <div className="flex border-b bg-white">
        <button
          onClick={() => setTab("category")}
          className={`flex-1 py-2.5 text-sm font-semibold flex items-center justify-center gap-1.5 border-b-2 transition ${
            tab === "category" ? "border-purple-600 text-purple-800 bg-purple-50" : "border-transparent text-gray-500 hover:bg-gray-50"
          }`}
          data-testid="picker-tab-category"
        >
          <Layers size={14}/> Por Categoría (flexible)
        </button>
        <button
          onClick={() => setTab("exact")}
          className={`flex-1 py-2.5 text-sm font-semibold flex items-center justify-center gap-1.5 border-b-2 transition ${
            tab === "exact" ? "border-blue-600 text-blue-800 bg-blue-50" : "border-transparent text-gray-500 hover:bg-gray-50"
          }`}
          data-testid="picker-tab-exact"
        >
          <Package size={14}/> SKU Exacto
        </button>
      </div>

      {tab === "category"
        ? <CategoryTab existingIds={existingCatIds} onPick={(c) => { onAddCategory(c); onClose(); }} />
        : <ExactTab existingIds={existingIds} onPick={(p) => { onAddExact(p); onClose(); }} />}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// TAB 1 · Elegir Categoría Odoo
// ─────────────────────────────────────────────────────────────────────────
function CategoryTab({ existingIds, onPick }) {
  const [q, setQ] = useState("");
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/v1/odoo/product-categories", { params: { limit: 500 } })
      .then((r) => setCats(r.data.items || []))
      .catch(() => toast.error("Error cargando categorías Odoo"))
      .finally(() => setLoading(false));
  }, []);

  const filtered = q.trim()
    ? cats.filter((c) => ((c.complete_name || c.name) || "").toLowerCase().includes(q.trim().toLowerCase()))
    : cats;

  return (
    <>
      <div className="p-3 border-b flex items-center gap-2">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
          <input
            type="text" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar categoría Odoo (FIBRA / ACCESORIOS / CONECTORES...)"
            className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm"
            data-testid="cat-search" autoFocus
          />
        </div>
        <span className="text-[10px] text-gray-500">{filtered.length}/{cats.length}</span>
      </div>
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-gray-400"/></div>
        ) : filtered.length === 0 ? (
          <div className="py-8 text-center text-gray-500 text-sm">Sin categorías coincidentes.</div>
        ) : filtered.map((c) => {
          const already = existingIds.includes(c.id);
          const fullName = c.complete_name || c.name;
          return (
            <button
              key={c.id}
              disabled={already}
              onClick={() => onPick(c)}
              className={`w-full flex items-center gap-3 px-4 py-2.5 hover:bg-purple-50 text-left border-b ${already ? "opacity-40 cursor-not-allowed" : ""}`}
              data-testid={`cat-pick-${c.id}`}
            >
              <div className="w-8 h-8 rounded-md flex items-center justify-center bg-purple-100 border border-purple-300 flex-shrink-0">
                <Layers size={14} className="text-purple-700"/>
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm truncate" style={{ color: "#0A3747" }} title={fullName}>{fullName}</div>
                <div className="text-[10px] text-gray-500 font-mono">Odoo id #{c.id}</div>
              </div>
              {already
                ? <span className="text-[9px] font-bold text-gray-400 uppercase">Ya en receta</span>
                : <ChevronRight size={14} className="text-gray-400"/>}
            </button>
          );
        })}
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// TAB 2 · Elegir SKU Exacto (mantiene flujo v3.9.7 en 2 pasos)
// ─────────────────────────────────────────────────────────────────────────
function ExactTab({ existingIds, onPick }) {
  const [step, setStep] = useState("categories");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categories, setCategories] = useState([]);
  const [catSearch, setCatSearch] = useState("");
  const [catLoading, setCatLoading] = useState(true);

  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setCatLoading(true);
    api.get("/v1/mesa/recipe/material-categories")
      .then((r) => setCategories(r.data.categories || []))
      .catch(() => toast.error("Error cargando categorías"))
      .finally(() => setCatLoading(false));
  }, []);

  const loadMaterials = async (category, query = "") => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (category && category !== "__all__") params.category = category;
      if (query.trim()) params.q = query.trim();
      const { data } = await api.get("/v1/mesa/recipe/materials", { params });
      setItems(data.items || []);
    } catch (e) { toast.error("Error cargando materiales"); }
    finally { setLoading(false); }
  };

  const openCategory = (name) => { setSelectedCategory(name); setQ(""); setStep("materials"); loadMaterials(name, ""); };
  const backToCategories = () => { setStep("categories"); setSelectedCategory(null); setItems([]); setQ(""); };

  const filteredCats = catSearch.trim()
    ? categories.filter((c) => c.category.toLowerCase().includes(catSearch.trim().toLowerCase()))
    : categories;

  if (step === "categories") {
    return (
      <>
        <div className="p-3 border-b flex items-center gap-2">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
            <input
              type="text" value={catSearch} onChange={(e) => setCatSearch(e.target.value)}
              placeholder="Buscar categoría de inventario..."
              className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm"
              data-testid="exact-cat-search" autoFocus
            />
          </div>
          <button
            onClick={() => openCategory("__all__")}
            className="text-xs font-semibold px-3 py-2 border rounded-lg hover:bg-gray-50"
            data-testid="exact-all-cats"
          >
            Todas
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {catLoading ? (
            <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-gray-400"/></div>
          ) : filteredCats.length === 0 ? (
            <div className="py-8 text-center text-gray-500">Sin categorías.</div>
          ) : filteredCats.map((c) => (
            <button
              key={c.category}
              onClick={() => openCategory(c.category)}
              className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-blue-50 text-left border-b transition"
              data-testid={`exact-cat-${c.category}`}
            >
              <div className="w-8 h-8 rounded-md flex items-center justify-center bg-blue-100 border border-blue-300 flex-shrink-0">
                <Package size={14} className="text-blue-700"/>
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm truncate" style={{ color: "#0A3747" }}>{c.category}</div>
                <div className="text-[10px] text-gray-500">{c.products_count.toLocaleString("es-EC")} productos</div>
              </div>
              <ChevronRight size={14} className="text-gray-400" />
            </button>
          ))}
        </div>
      </>
    );
  }

  return (
    <>
      <div className="p-3 border-b flex items-center gap-2">
        <button onClick={backToCategories} className="text-xs font-semibold text-blue-700 hover:underline" data-testid="exact-back">← Categorías</button>
        <ChevronRight size={12} className="text-gray-400" />
        <span className="text-xs font-bold text-[#0A3747]">{selectedCategory === "__all__" ? "Todas" : selectedCategory}</span>
        <div className="flex-1">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
            <input
              type="text" value={q} onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && loadMaterials(selectedCategory, q)}
              placeholder="Buscar material..."
              className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm"
              data-testid="exact-mat-search"
            />
          </div>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto">
        {loading ? <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-gray-400"/></div>
          : items.length === 0 ? <div className="py-8 text-center text-gray-500">Sin resultados.</div>
          : items.map((p) => {
              const already = existingIds.includes(p.product_code);
              return (
                <button key={p.product_id} disabled={already} onClick={() => onPick(p)}
                  className={`w-full flex items-center gap-3 px-4 py-2 hover:bg-blue-50 text-left border-b ${already ? "opacity-40 cursor-not-allowed" : ""}`}
                  data-testid={`exact-pick-${p.product_id}`}>
                  <Package size={16} className="text-gray-400"/>
                  <div className="flex-1">
                    <div className="font-semibold text-sm" style={{ color: "#0A3747" }}>{p.product_name}</div>
                    <div className="text-[10px] text-gray-500">{p.product_code} · {p.category || "—"}</div>
                  </div>
                  <div className="text-right text-xs">
                    <div className="font-semibold text-emerald-700">{fmt(p.total_available)}</div>
                    <div className="text-[10px] text-gray-400">on-hand {fmt(p.total_onhand)}</div>
                  </div>
                </button>
              );
            })}
      </div>
    </>
  );
}

function fmt(n) {
  const v = Number(n || 0);
  return v.toLocaleString("es-EC", { timeZone: "America/Guayaquil", maximumFractionDigits: 2 });
}

function NumberEditor({ value, onSave, saving, testid }) {
  const [v, setV] = useState(value);
  useEffect(() => { setV(value); }, [value]);
  return (
    <div className="relative inline-block">
      <input
        type="number" min="0" step="0.5" value={v ?? 0}
        onChange={(e) => setV(e.target.value)}
        onBlur={() => { if (Number(v) !== Number(value)) onSave(v); }}
        className="w-14 text-center px-1 py-0.5 border rounded text-sm tabular-nums"
        data-testid={testid}
      />
      {saving && <Loader2 size={10} className="absolute -right-3 top-1/2 -translate-y-1/2 animate-spin text-blue-500"/>}
    </div>
  );
}
